/* eslint-disable react/jsx-no-bind */
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  TextField,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
} from '@mui/material';
import React, { useState, Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { mockKPIData, mockTransactions } from '../../data/mockData';
import dashboardService from '../../services/dashboardService';
import { tokens } from '../../theme';
import { formatToIST } from '../../utils/dateFormatter';
import logger from '../../utils/logger';
import { statsStorage } from '../../utils/statsStorage';
import Header from '../Header';

const LazyLineChart = React.lazy(() => import('../LineChart'));
const LazyStatBox = React.lazy(() => import('../StatBox'));

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [isShipmentsView, setIsShipmentsView] = useState(true);
  const [, setCustomerStats] = useState({
    total: 0,
    kycDone: 0,
    kycPending: 0,
  });
  const [kpiData, setKpiData] = useState(mockKPIData);
  const [loading, setLoading] = useState(true);

  const filteredTransactions = mockTransactions.filter(
    (transaction) =>
      transaction.txId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.user.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    fetchCustomerStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCustomerStats = async () => {
    setLoading(true);
    try {
      // console.log('Fetching real customer data...');
      const statsData = await dashboardService.getCustomerStats();
      // console.log('Raw API response:', statsData);
      const stats = {
        total: statsData.totalUsers,
        kycDone: statsData.kycCompleted,
        kycPending: statsData.kycPending,
      };

      // console.log('Processed stats:', stats);

      setCustomerStats(stats);
      updateKpiData(stats);
      statsStorage.set(stats);
    } catch (error) {
      logger.error('Error fetching customer stats:', error);
      const fallbackStats = { total: 0, kycDone: 0, kycPending: 0 };
      setCustomerStats(fallbackStats);
      updateKpiData(fallbackStats);
    } finally {
      setLoading(false);
    }
  };

  const updateKpiData = (stats) => {
    // console.log('Updating tiles with:', stats);
    const updatedKpiData = [...mockKPIData];

    // Find and update specific tiles
    const totalUsersIndex = updatedKpiData.findIndex(
      (kpi) => kpi.subtitle === 'Total Users',
    );
    const kycDoneIndex = updatedKpiData.findIndex(
      (kpi) => kpi.subtitle === 'KYC Done',
    );
    const kycPendingIndex = updatedKpiData.findIndex(
      (kpi) => kpi.subtitle === 'KYC Pending',
    );

    if (totalUsersIndex !== -1) {
      updatedKpiData[totalUsersIndex] = {
        ...updatedKpiData[totalUsersIndex],
        title: stats.total.toString(),
      };
    }
    if (kycDoneIndex !== -1) {
      updatedKpiData[kycDoneIndex] = {
        ...updatedKpiData[kycDoneIndex],
        title: stats.kycDone.toString(),
      };
    }
    if (kycPendingIndex !== -1) {
      updatedKpiData[kycPendingIndex] = {
        ...updatedKpiData[kycPendingIndex],
        title: stats.kycPending.toString(),
      };
    }
    // console.log('Updated KPI data:', updatedKpiData);
    setKpiData(updatedKpiData);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        m: { xs: 1, sm: 2, md: 3 },
        ml: { xs: 1, sm: 2, md: 4 },
        maxWidth: '100%',
      }}
    >
      <div className="row " style={{ height: '100%' }}>
        <div>
          {/* Header */}
          <Card
            sx={{
              boxShadow: 2,
              borderRadius: 2,
              margin: 2,
              marginLeft: 0,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingLeft: 2,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
              >
                <Header title="DASHBOARD" subtitle="" />
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(4, 1fr)',
                  lg: 'repeat(12, 1fr)',
                }}
                gridAutoRows="minmax(120px, auto)"
                gap={{ xs: '10px', sm: '15px', md: '20px' }}
                marginBottom={{ xs: '20px', md: '30px' }}
              >
                {/* ROW 1 */}
                {kpiData.map((eachkpi, index) => (
                  <Box
                    gridColumn={{
                      xs: 'span 1',
                      sm: 'span 1',
                      md: 'span 1',
                      lg: 'span 3',
                    }}
                    gridRow={{ lg: index < 4 ? '1' : '2' }}
                    backgroundColor={colors.primary[400]}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    key={eachkpi.id}
                    boxShadow={2}
                    borderRadius={2}
                    bgcolor={'white'}
                    minHeight={{ xs: '100px', sm: '120px' }}
                  >
                    <Suspense fallback={<div>Loading...</div>}>
                      <LazyStatBox
                        title={
                          loading &&
                          (eachkpi.subtitle === 'Total Users' ||
                            eachkpi.subtitle === 'KYC Done' ||
                            eachkpi.subtitle === 'KYC Pending')
                            ? '...'
                            : eachkpi.title
                        }
                        subtitle={eachkpi.subtitle}
                        progress={eachkpi.progress}
                        increase={eachkpi.increase}
                        icon={React.cloneElement(eachkpi.icon, {
                          sx: {
                            color: colors.swiftcarry[100],
                            fontSize: '26px',
                          },
                        })}
                        onClick={
                          eachkpi.subtitle === 'Total Users'
                            ? () => navigate('/customers')
                            : eachkpi.subtitle === 'KYC Done'
                              ? () => navigate('/customers?filter=completed')
                              : eachkpi.subtitle === 'KYC Pending'
                                ? () => navigate('/customers?filter=pending')
                                : undefined
                        }
                      />
                    </Suspense>
                  </Box>
                ))}

                {/* ROW 3 - Revenue Generated */}
                <Box
                  gridColumn={{
                    xs: 'span 1',
                    sm: 'span 2',
                    md: 'span 4',
                    lg: 'span 7',
                  }}
                  boxShadow={2}
                  borderRadius={2}
                  bgcolor={'white'}
                  minHeight="400px"
                >
                  <Box
                    mt="25px"
                    p="0 30px"
                    display="flex "
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight="600"
                        color={colors.grey[100]}
                      >
                        Revenue Generated
                      </Typography>
                      <Typography
                        variant="h3"
                        fontWeight="bold"
                        color={colors.swiftcarry[100]}
                      >
                        $59,342.32
                      </Typography>
                    </Box>
                    <Box>
                      <IconButton>
                        <DownloadOutlinedIcon
                          sx={{
                            fontSize: '26px',
                            color: colors.swiftcarry[100],
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    height={{ xs: '250px', sm: '350px', md: '500px' }}
                    m="-20px 0 0 0"
                  >
                    <Suspense fallback={<div>Loading...</div>}>
                      <LazyLineChart isDashboard={true} />
                    </Suspense>
                  </Box>
                </Box>

                {/* ROW 3 - Recent Transits Section */}
                <Box
                  gridColumn={{
                    xs: 'span 1',
                    sm: 'span 2',
                    md: 'span 4',
                    lg: 'span 5',
                  }}
                  backgroundColor={colors.primary[400]}
                  overflow="auto"
                  position="relative"
                  boxShadow={2}
                  borderRadius={2}
                  bgcolor={'white'}
                  minHeight="400px"
                  maxHeight="500px"
                >
                  {/* Header with Toggle and Search */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    borderBottom={'1px solid #ccc'}
                    backgroundColor={colors.primary[400]}
                    color={colors.swiftcarry[100]}
                    p="15px"
                    position="sticky"
                    top="0"
                    zIndex="10"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography
                        color={colors.grey[100]}
                        variant="h5"
                        fontWeight="600"
                      >
                        Recent {isShipmentsView ? 'Shipments' : 'Transits'}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isShipmentsView}
                            onChange={(e) =>
                              setIsShipmentsView(e.target.checked)
                            }
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: colors.swiftcarry[100],
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                {
                                  backgroundColor: colors.swiftcarry[100],
                                },
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" color={colors.grey[100]}>
                            {isShipmentsView ? 'Shipments' : 'Transits'}
                          </Typography>
                        }
                        sx={{ margin: 0 }}
                      />
                    </Box>
                    <TextField
                      variant="outlined"
                      placeholder={`Search by ${
                        isShipmentsView ? 'Shipment' : 'Transit'
                      } Id or User`}
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        minWidth: { xs: '120px', sm: '200px', md: '250px' },
                        width: { xs: '100%', sm: 'auto' },
                      }}
                    />
                  </Box>

                  {/* Filtered Transactions */}
                  {filteredTransactions.map((transaction, i) => (
                    <Box
                      key={`${transaction.txId}-${i}`}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      borderBottom={'1px solid #ccc'}
                      p="15px"
                    >
                      <Box>
                        <Typography
                          color={colors.swiftcarry[100]}
                          variant="h5"
                          fontWeight="600"
                        >
                          {transaction.txId}
                        </Typography>
                        <Typography color={colors.grey[100]}>
                          {transaction.user}
                        </Typography>
                      </Box>
                      <Box color={colors.grey[100]}>
                        {formatToIST(transaction.date)}
                      </Box>
                      <Box
                        backgroundColor={colors.swiftcarry[100]}
                        p="5px 10px"
                        borderRadius="4px"
                      >
                        ${transaction.cost}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Grid and Charts */}
        </div>
      </div>
    </Box>
  );
};

export default Dashboard;
